import type { AppleCredential } from "@/api/client";

// Sign in with Apple on the website: Apple's own script opens Apple's sign-in in a popup.
// The page identifies itself with the Services ID made in the Apple Developer account
// (return URL registered there: the site's /login page; with the popup it is not navigated to).
// The backend (/login/apple) checks the token it gets back.

const APPLE_SCRIPT_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_SERVICES_ID = "app.oitava.signin";

interface AppleIdSdk {
  auth: {
    init: (config: Record<string, unknown>) => void;
    signIn: () => Promise<{ authorization: { id_token: string; code: string } }>;
  };
}

declare global {
  interface Window {
    AppleID?: AppleIdSdk;
  }
}

function loadAppleScript(): Promise<AppleIdSdk> {
  return new Promise((resolve, reject) => {
    if (window.AppleID) return resolve(window.AppleID);
    const script = document.createElement("script");
    script.src = APPLE_SCRIPT_URL;
    script.async = true;
    script.onload = () =>
      window.AppleID ? resolve(window.AppleID) : reject(new Error("Apple sign-in did not load."));
    script.onerror = () => reject(new Error("Apple sign-in did not load."));
    document.head.appendChild(script);
  });
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

/** Opens Apple's popup. Returns what the backend needs, or null when the person closes it. */
export async function getAppleCredential(): Promise<AppleCredential | null> {
  const apple = await loadAppleScript();
  // Apple puts the SHA-256 of this value in the token; the backend checks it matches, so an old
  // token cannot be replayed into a new sign-in.
  const nonce = crypto.randomUUID();
  apple.auth.init({
    clientId: APPLE_SERVICES_ID,
    scope: "email",
    redirectURI: `${window.location.origin}/login`,
    usePopup: true,
    nonce: await sha256Hex(nonce),
  });
  try {
    const { authorization } = await apple.auth.signIn();
    return {
      identityToken: authorization.id_token,
      authorizationCode: authorization.code,
      nonce,
    };
  } catch (error) {
    const code = (error as { error?: string }).error;
    if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") return null;
    throw error;
  }
}
